import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import classNames from "classnames";
import { useFormContext } from "react-hook-form";

import styles from "./index.module.scss";
import { CircleChecked, AlertOctagon, } from "../../components/Icons/general-interface-2";
import { SectionListType } from "../../types/sectionType";
import { useSectionsValid } from "../../hooks/useSectionsValid";
import { useClientFileContext } from "../../contexts/ClientFileProvider";

type PropsType = {
  title: string;
  sections: SectionListType;
  selectedSection: string;
  setSelectedSection: Dispatch<SetStateAction<string>>;
  waitingForInitialValidation?: boolean;
};

const FormNavigation = ({
  title,
  sections,
  selectedSection,
  setSelectedSection,
  waitingForInitialValidation,
}: PropsType) => {
  const { formState, setValue, getValues, trigger } = useFormContext();
  const { formMode } = useClientFileContext();
  const sectionsValid = useSectionsValid(true, true);
  const touched = Object.fromEntries(
    Object.entries({
      section1: useState(getValues("info.section1-touched")),
      section2: useState(getValues("info.section2-touched")),
      section2a: useState(getValues("info.section2a-touched")),
      section2b: useState(getValues("info.section2b-touched")),
      section2c: useState(getValues("info.section2c-touched")),
      section2d: useState(getValues("info.section2d-touched")),
      section3: useState(getValues("info.section3-touched")),
      section4Alt: useState(getValues("info.section4Alt-touched")),
      section4: useState(getValues("info.section4-touched")),
    }).map(([key, [state, setState]]) => [key, { state, setState }])
  );

  const formIsEditable = formMode === "edit" || formMode === "initiateSigning";

  // We'll track 'touched' section status for the authorizer only
  useEffect(() => {
    const runEffect = formIsEditable && !waitingForInitialValidation;
    if (runEffect) {
      // Immediately update the form state to touched when a section is loaded
      setValue(`info.${selectedSection}-touched`, selectedSection !== "section4Alt");
    }
    return () => {
      if (runEffect) {
        // Delay updating the local touched state and triggering of validation until
        // a different section is selected or this component is unmounted
        trigger(selectedSection);
        touched[selectedSection].setState(selectedSection !== "section4Alt");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, waitingForInitialValidation]);

  const getIcon = (section: string) => {
    if (["section4Alt"].includes(section)) return null;
    const showIcon = !waitingForInitialValidation && (!formIsEditable || !!touched[section].state);
    if (showIcon && sectionsValid[section])
      return <CircleChecked className={styles.iconValidated} />;
    if (showIcon && formState.errors[section])
      return <AlertOctagon className={styles.iconError} />;
    return null;
  };

  return (
    <div className={styles.content}>
      <nav className={styles.form}>
        <div className={styles.formHeader}>
          <div className={styles.formTitle}>{title}</div>
        </div>
        {Object.keys(sections).map((i) => (
          <React.Fragment key={i}>
            {!sections[i].hidden && (
              <button
                key={i}
                type="button"
                className={classNames(styles.formItem, {
                  [styles.formItemSelected]: selectedSection === i,
                  [styles.formSubItem]: sections[i].subsection === true,
                  [styles.disabled]: sections[i].disabled,
                })}
                onClick={() => {
                  setSelectedSection(i);
                  window.scrollTo(0, 0);
                }}
                disabled={sections[i].disabled}
              >
                <span className={styles.formText}>
                  {sections[i].sidebarText || sections[i].text}
                </span>
                {getIcon(i)}
              </button>
            )}
          </React.Fragment>
        ))}
      </nav>
    </div>
  );
};
export default FormNavigation;
